import React, { createContext, useContext, useState, useEffect } from 'react';
import { getBrowserLanguage, isLanguageAvailable } from '../config/languages';

interface TranslationData {
  [key: string]: any;
}

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (langCode: string) => void;
  t: (key: string, replacements?: { [key: string]: string | number }) => any;
  isLoaded: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang && isLanguageAvailable(savedLang)) {
      return savedLang;
    }
    const browserLang = getBrowserLanguage();
    return isLanguageAvailable(browserLang) ? browserLang : 'it_IT';
  });
  
  const [translations, setTranslations] = useState<TranslationData>({});
  const [isLoaded, setIsLoaded] = useState(false);

  const loadTranslations = async (langCode: string) => {
    setIsLoaded(false);
    try {
      const fileName = `/${langCode}.json`;
      const response = await fetch(fileName);
      
      if (response.ok) {
        const data = await response.json();
        setTranslations(data);
        setIsLoaded(true);
      } else {
        throw new Error('Failed to fetch translations');
      }
    } catch (error) {
      console.error('Error loading translations:', error);
      // Fallback to module import
      try {
        let modulePath = '../../it_IT.json'; // default
        if (langCode === 'en_GB') {
          modulePath = '../../en_GB.json';
        } else if (langCode === 'de_DE') {
          modulePath = '../../de_DE.json';
        } else if (langCode === 'fr_FR') {
          modulePath = '../../fr_FR.json';
        }
        const module = await import(modulePath);
        setTranslations(module.default);
        setIsLoaded(true);
      } catch (moduleError) {
        console.error('Error loading translations module:', moduleError);
        // Set minimal fallback
        setTranslations({
          common: { loading: 'Loading...' },
          header: { siteName: 'EscortFun.net' }
        });
        setIsLoaded(true);
      }
    }
  };

  useEffect(() => {
    loadTranslations(currentLanguage);
  }, [currentLanguage]);

  const setLanguage = (langCode: string) => {
    if (isLanguageAvailable(langCode)) {
      setCurrentLanguage(langCode);
      localStorage.setItem('selectedLanguage', langCode);
    }
  };

  const t = (key: string, replacements?: { [key: string]: string | number }): any => {
    // Don't try to translate if translations haven't loaded yet
    if (!isLoaded || Object.keys(translations).length === 0) {
      return key;
    }

    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (value === undefined || value === null) {
        break;
      }
      value = value[k];
    }

    // Only warn about missing translations if we've fully loaded translations
    // and the value is actually missing (undefined or null)
    if (value === undefined || value === null) {
      console.warn(`[Translation] MISSING - Language: ${currentLanguage}, Key: ${key}`);
      return key;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value;
    }

    // Convert non-strings to strings
    if (typeof value !== 'string') {
      return String(value);
    }

    // Apply replacements if provided
    if (replacements) {
      let result: string = value;
      for (const [placeholder, replacement] of Object.entries(replacements)) {
        result = result.replace(new RegExp(`{${placeholder}}`, 'g'), String(replacement));
      }
      return result;
    }

    return value;
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t, isLoaded }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};